// Regras puras do orçamento — espelham core/sql/functions/orcamento.sql.
// Tudo aqui é determinístico e testável: situação, datas e o texto que vai
// para o cliente. Nada de Supabase neste arquivo.

import type { CanalOrcamento } from '@core/types';
import { brl, qtd } from './formato';

export type SituacaoOrcamento = 'aberto' | 'convertido' | 'vencido';

/**
 * Mesmo corte da RPC converter_orcamento: o orçamento vale ATÉ o dia da
 * validade, inclusive — vence no dia seguinte. Comparação de strings
 * 'YYYY-MM-DD' é comparação de datas, sem objeto Date (e sem fuso).
 */
export function situacaoOrcamento(
  o: { convertido_em: string | null; valido_ate: string },
  hojeISO: string,
): SituacaoOrcamento {
  if (o.convertido_em) return 'convertido';
  return o.valido_ate < hojeISO ? 'vencido' : 'aberto';
}

/**
 * 'YYYY-MM-DD' → 'DD/MM/YYYY' sem passar por Date: date-only em `new Date()`
 * vira meia-noite UTC, que em Rio Grande (UTC-3) é o dia ANTERIOR — a validade
 * impressa estaria errada.
 */
export function dataCurtaDeISO(dataISO: string): string {
  const [ano, mes, dia] = dataISO.split('-');
  if (!ano || !mes || !dia) return dataISO;
  return `${dia}/${mes}/${ano}`;
}

/** Validade padrão da UI: hoje + N dias, em 'YYYY-MM-DD' local (7 espelha a RPC). */
export function validadePadrao(hojeISO: string, dias = 7): string {
  const [ano, mes, dia] = hojeISO.split('-').map(Number);
  const d = new Date(ano ?? 1970, (mes ?? 1) - 1, (dia ?? 1) + dias);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export interface ItemTextoOrcamento {
  descricao: string;
  quantidade: number;
  preco_unit: number;
  subtotal: number;
}

export interface CabecalhoTextoOrcamento {
  codigo: string;
  cliente_nome: string | null;
  desconto: number;
  total: number;
  valido_ate: string;   // 'YYYY-MM-DD'
  vendedor: string | null;
}

/**
 * Mensagem pronta para WhatsApp — o canal da loja. `*negrito*` é a marcação
 * do próprio WhatsApp; a soma das peças só aparece quando há desconto,
 * senão repetiria o total.
 */
export function textoOrcamento(o: CabecalhoTextoOrcamento, itens: ItemTextoOrcamento[]): string {
  const linhas = [
    '*SERGINHO AUTOPEÇAS*',
    `Orçamento ${o.codigo}${o.cliente_nome ? ` — ${o.cliente_nome}` : ''}`,
    '',
    ...itens.map((i) => `${qtd(i.quantidade)}× ${i.descricao} — ${brl(i.subtotal)}`),
    '',
  ];
  if (o.desconto > 0) {
    linhas.push(`Peças: ${brl(o.total + o.desconto)}`);
    linhas.push(`Desconto: ${brl(o.desconto)}`);
  }
  linhas.push(`*Total: ${brl(o.total)}*`);
  linhas.push('');
  linhas.push(`Válido até ${dataCurtaDeISO(o.valido_ate)}. Peças sujeitas a disponibilidade de estoque.`);
  if (o.vendedor) linhas.push(`Atendimento: ${o.vendedor}`);
  return linhas.join('\n');
}

/**
 * Link wa.me com o texto pronto. Sem fone, abre o WhatsApp para escolher o
 * contato. Com fone, normaliza para E.164 brasileiro: só dígitos e DDI 55 —
 * cuidado com DDD 55 (Santa Maria): '55 9xxxx-xxxx' tem 11 dígitos e ainda
 * precisa do DDI na frente; um número já internacional tem 12-13.
 */
export function linkWhatsApp(texto: string, fone?: string | null): string {
  const digitos = (fone ?? '').replace(/\D/g, '');
  const destino = !digitos
    ? ''
    : digitos.startsWith('55') && digitos.length >= 12
      ? digitos
      : `55${digitos}`;
  return `https://wa.me/${destino}?text=${encodeURIComponent(texto)}`;
}

/**
 * Os canais da cotação (049) num lugar só: a Venda oferece, a lista filtra e
 * o documento imprime a partir daqui. "Balcão" primeiro porque é o caso de
 * quem está com o cliente na frente — e o padrão de quem não escolhe.
 */
export const CANAIS_ORCAMENTO = [
  { id: 'balcao', rotulo: 'Balcão' },
  { id: 'whatsapp', rotulo: 'WhatsApp' },
  { id: 'telefone', rotulo: 'Telefone' },
] as const satisfies ReadonlyArray<{ id: CanalOrcamento; rotulo: string }>;

export const rotuloDoCanal = (c: CanalOrcamento): string =>
  CANAIS_ORCAMENTO.find((x) => x.id === c)?.rotulo ?? c;
